import AOS from 'aos';
import Splide from '@splidejs/splide';
import { unitlessBreakpoints } from './breakpoints';

const selectors = {
  mainCarousel: '[data-splide-carousel]',
  progressBar: '[data-progress-bar]',
};

/**
 * Drags that start inside an overflowing swatch strip belong to that strip, not to the
 * carousel around it.
 *
 * A product card's colour swatches are their own Splide, so without this a sideways drag
 * across them scrolls both at once — the swatches under your finger and the whole row of
 * cards behind them. Scoped to `.is-overflow` because Splide only adds that class when the
 * strip actually has somewhere to scroll; a short strip stays transparent to the drag and
 * the parent carousel keeps working normally over the top of it.
 */
const SWATCH_STRIP_NO_DRAG = '[data-product-item-swatches-splide].is-overflow *';

const initCarousel = (el, options = {}, replaceOptions = false) => {
  const carousel = el.querySelector(selectors.mainCarousel);
  let carouselOptions = {};

  if (carousel) {
    carouselOptions = {
      perPage: 2,
      perMove: 1,
      gap: '1rem',
      mediaQuery: 'min',
      breakpoints: {
        [unitlessBreakpoints.md]: {
          perPage: 3,
        },
        [unitlessBreakpoints.lg]: {
          perPage: 4,
        },
      },
      ...options,
    };

    if (replaceOptions) {
      carouselOptions = { ...options };
    }

    const { progressBar: enableProgressBar, ...splideOptions } =
      carouselOptions;
    const useProgressBar =
      carousel.hasAttribute('data-splide-progress-bar') ||
      enableProgressBar === true;

    // `noDrag` leads so a caller can still override it deliberately; every carousel that can
    // hold a product card gets the guard by default rather than opting in section by section.
    const splide = new Splide(carousel, {
      noDrag: SWATCH_STRIP_NO_DRAG,
      ...splideOptions,
    });

    if (useProgressBar) {
      const progressBar = carousel.querySelector(selectors.progressBar);
      if (progressBar) {
        const updateProgress = () => {
          const end = splide.Components.Controller.getEnd() + 1;
          const rate = Math.min((splide.index + 1) / end, 1);
          progressBar.style.transform = `scaleX(${rate})`;
        };
        // Before mount() so the initial `mounted` event runs this (same as MainHeader megamenu).
        splide.on('mounted move', updateProgress);
      }
    }

    const syncArrowsHiddenClass = () => {
      const arrowsEl = carousel.querySelector('.splide__arrows');
      if (!arrowsEl) {
        return;
      }
      const { perPage } = splide.options;
      const shouldHide =
        typeof perPage === 'number' && splide.length <= perPage;
      // Class uses a single hyphen before `hidden` (not BEM `--`) so Tailwind
      // `has-[.splide__arrows-hidden]:…` parses; `--` inside `has-[…]` breaks.
      arrowsEl.classList.toggle('splide__arrows-hidden', shouldHide);
      if (shouldHide) {
        arrowsEl.setAttribute('data-arrows-hidden', '');
      } else {
        arrowsEl.removeAttribute('data-arrows-hidden');
      }
    };

    splide.mount();

    if (splide.length <= splide.options.perPage) {
      splide.options = {
        arrows: false,
      };
    }

    syncArrowsHiddenClass();
    splide.on('updated', syncArrowsHiddenClass);

    splide.on('hidden', () => {
      if (splide.length > splide.options.perPage) {
        splide.options = {
          arrows: true,
        };
      }
      syncArrowsHiddenClass();
    });
    splide.on('visible', () => {
      if (splide.length <= splide.options.perPage) {
        splide.options = {
          arrows: false,
        };
      }
      syncArrowsHiddenClass();
    });

    AOS.refresh();
    return splide;
  }

  return null;
};

export default initCarousel;
